import { GetRecommendationByIdApiResponse } from '@/api/recommendations'
import { SupportedCustomizationOptionsKeys } from '@/types/recommendation'
import { camelToSnakeCase, snakeToCamelCase } from '@/utils'

export const getMismatchedOptions = (
  data: GetRecommendationByIdApiResponse,
  recommendationProductId: string,
): {
  name: SupportedCustomizationOptionsKeys
  value: boolean
}[] => {
  const productData = data.recommendationProducts.find(
    (product) => product.recommendationProductId === recommendationProductId,
  )
  const customizationOptions = data.customization?.options || []
  const searchCriteriaOptions = data.searchCriteria?.options || []

  const selectedYesNoValuesOptions =
    customizationOptions.reduce((acc, customizationOption) => {
      customizationOption.attributes.forEach((attribute) => {
        acc[snakeToCamelCase(attribute)] = !!searchCriteriaOptions.find(
          (searchCriteriaOption) =>
            searchCriteriaOption.name === customizationOption.name,
        )?.value
      })
      return acc
    }, {} as Record<string, boolean>) || {}

  const mismatchedYesNoValues = Object.entries(selectedYesNoValuesOptions)
    .filter(([key, selectedValue]) => {
      const actualValue = !!productData?.yesNoValues?.[key]
      return actualValue !== selectedValue
    })
    .map(([key]) => ({
      name: camelToSnakeCase(key),
      value: !!productData?.yesNoValues?.[key],
    }))

  const mismatchedCustomizationOptions = customizationOptions.reduce(
    (customizationOptionsAcc, customizationOption) => {
      const isOptionMismatched = customizationOption.attributes.reduce(
        (isOptionMismatchedValueAcc, attribute) => {
          const currentYesNoValue = mismatchedYesNoValues.find(
            (yesNoValue) => yesNoValue.name === attribute,
          )
          if (currentYesNoValue) {
            return true
          }
          return isOptionMismatchedValueAcc
        },
        false,
      )

      if (isOptionMismatched) {
        customizationOptionsAcc.push({
          name: customizationOption.name,
          value: !searchCriteriaOptions?.find(
            (option) => option.name === customizationOption.name,
          )?.value,
        })
      }

      return customizationOptionsAcc
    },
    [] as {
      name: SupportedCustomizationOptionsKeys
      value: boolean
    }[],
  )

  return mismatchedCustomizationOptions || []
}

export const isProductCustomizationMismatched = (
  data: GetRecommendationByIdApiResponse,
  recommendationProductId: string,
) => {
  return getMismatchedOptions(data, recommendationProductId).length > 0
}

export const isAnyProductMismatched = (
  data: GetRecommendationByIdApiResponse,
) => {
  return data.recommendationProducts.some((product) =>
    isProductCustomizationMismatched(data, product.recommendationProductId),
  )
}
